/**
 * Copyright 2020 New Light Technologies, Inc.
 *
 * With Supporting Sponsorship from the Federal Emergency Management Agency (Contract: GSA Stars II GS-06F-0968Z)
 * In accordance with FAR 52.227-14(c)(iii), New Light Technologies, Inc. grants to the Government and others acting on its behalf a paid-up, nonexclusive, irrevocable, worldwide license in such copyrighted computer software and data to reproduce, prepare derivative works, and perform publicly and display publicly (but not to distribute copies to the public) by or on behalf of the Government.
 * Any other use, distribution, reproduction, modification, or publication without the prior express written authorization of New Light Technologies, Inc. is strictly prohibited.
 * All other rights are reserved by their respective copyright holders.
 *
 */
import type { FeatureCollection, GeoJsonProperties, Polygon } from 'geojson';
import { atom } from 'jotai';
import { atomFamily } from 'jotai-family';
import { useSetAtom } from 'jotai';
import React from 'react';

import { userAtom } from '../auth/userAtoms';
import { fetchFlatGeobuf } from '../services/featuresApi';

const TABLE_MAP = {
  perimeter: 'wow_threats',
  post: 'wow_post',
} as const;

export async function asyncGetWowData<P extends GeoJsonProperties = GeoJsonProperties>(
  type: 'perimeter' | 'post'
): Promise<FeatureCollection<Polygon, P>> {
  const table = TABLE_MAP[type];
  return fetchFlatGeobuf<Polygon, P>(table);
}

export const refreshTriggerAtom = atom(0);

/**
 * WoW current-table chunks.
 *
 * `enabled` is the caller's statement that it will use the data. The map hooks
 * are mounted by the shared Map component on every route, including incident
 * views where the WoW layers are never drawn, so an unconditional read of this
 * selector pulled both WoW tables on every incident view. Consumers that only
 * exist on the dashboard (the sidebar list, the legend) pass `true`.
 */
export const wowChunksSelector = atomFamily(
  (params: { type: 'post' | 'perimeter'; enabled: boolean }) =>
    atom(async get => {
      get(refreshTriggerAtom);

      if (!params.enabled) return [];

      const user = get(userAtom);
      if (!user) return [];

      const data = await asyncGetWowData(params.type);
      return [data];
    }),
  (a, b) => a.type === b.type && a.enabled === b.enabled
);

export const usePeriodicWowDataRefresh = () => {
  const setRefreshTrigger = useSetAtom(refreshTriggerAtom);

  React.useEffect(() => {
    const interval = setInterval(() => {
      setRefreshTrigger(currentValue => currentValue + 1);
    }, 900000);

    return () => clearInterval(interval);
  }, [setRefreshTrigger]);
};
